import * as React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';

interface DemoTooltipWrapperProps {
  /**
   * Whether to enable the tooltip wrapper
   * Typically this should be `isDemoMode`
   */
  enabled: boolean;
  /**
   * Message to display in the tooltip
   * @default "Editing not allowed in demo version"
   */
  message?: string;
  /**
   * The element(s) to wrap with the tooltip
   */
  children: React.ReactElement;
}

/**
 * Conditionally wraps children with a tooltip when in demo mode
 *
 * Usage:
 * ```tsx
 * <DemoTooltipWrapper enabled={isDemoMode}>
 *   <button disabled={isDemoMode}>
 *     Edit
 *   </button>
 * </DemoTooltipWrapper>
 * ```
 *
 * @param enabled - Whether to show the tooltip (e.g., isDemoMode)
 * @param message - Custom tooltip message
 * @param children - Element to wrap
 */
export const DemoTooltipWrapper: React.FC<DemoTooltipWrapperProps> = ({
  enabled,
  message = 'Editing not allowed in demo version',
  children,
}) => {
  if (!enabled) {
    return children;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent>
          <p>{message}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
